import { useEffect } from "react";
import { Navigate, Route, Routes, useLocation, useNavigate } from "react-router-dom";
import { SdkInit } from "./components/SdkInit";
import { useMe } from "./api/hooks";
import { TabBar } from "./components/TabBar";
import { getStartParam, hasTelegramContext, webApp } from "./lib/telegram";
import { useCanGoBack } from "./lib/use-can-go-back";
import { ArticleScreen } from "./screens/ArticleScreen";
import { ArticlesScreen } from "./screens/ArticlesScreen";
import { CheckoutScreen } from "./screens/CheckoutScreen";
import { FaqScreen } from "./screens/FaqScreen";
import { HomeScreen } from "./screens/HomeScreen";
import { OrderPlacedScreen } from "./screens/OrderPlacedScreen";
import { OrderScreen } from "./screens/OrderScreen";
import { OrdersScreen } from "./screens/OrdersScreen";
import { ProfileEditScreen } from "./screens/ProfileEditScreen";
import { ProfileScreen } from "./screens/ProfileScreen";
import { AdminOrderScreen } from "./screens/admin/AdminOrderScreen";
import { AdminOrdersScreen } from "./screens/admin/AdminOrdersScreen";
import { OnboardingScreen } from "./screens/onboarding/OnboardingScreen";

/** Вкладки таб-бара. На вложенных экранах его прячем — там нативная кнопка «назад». */
const TAB_ROUTES = ["/", "/articles", "/profile"];

/**
 * Куда онбординг не вмешивается: сам онбординг, заказ из уведомления бота
 * (человек уже заказывал — данные ему сейчас не нужны) и админка.
 */
const NO_ONBOARDING = [/^\/onboarding/, /^\/orders\//, /^\/admin/];

/** Глубокая ссылка t.me/<бот>/<app>?startapp=order_<номер> → экран заказа. */
function startRoute(): string | null {
  const match = /^order_([A-Za-z0-9-]{1,32})$/.exec(getStartParam() ?? "");
  return match ? `/orders/${match[1]}` : null;
}

export function App() {
  const location = useLocation();
  const navigate = useNavigate();
  const me = useMe();
  const isTab = TAB_ROUTES.includes(location.pathname);
  const canGoBack = useCanGoBack();

  /**
   * Нативная кнопка «назад» Telegram вместо своей в шапке. На вкладках
   * прячем — уходить с корневого экрана некуда; на входе по прямой ссылке
   * (уведомление бота открывает сразу заказ) истории нет, и стрелка
   * выглядела бы рабочей, ничего не делая.
   */
  useEffect(() => {
    const app = webApp();
    if (!app) return;

    const goBack = () => navigate(-1);
    if (isTab || !canGoBack) {
      app.BackButton.hide();
      return;
    }
    app.BackButton.show();
    app.BackButton.onClick(goBack);
    return () => app.BackButton.offClick(goBack);
  }, [isTab, canGoBack, navigate]);

  // Переход по startapp — один раз, при первом рендере корня.
  useEffect(() => {
    const target = startRoute();
    if (target && location.pathname === "/") navigate(target, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Онбординг показывается, пока пользователь его не прошёл или не пропустил
   * (`onboarded` с сервера). Вне Telegram профиля нет — витрину можно
   * смотреть сразу, онбординг открывается по адресу /onboarding.
   */
  const onboardingApplies =
    hasTelegramContext() && !NO_ONBOARDING.some((pattern) => pattern.test(location.pathname));

  /*
   * Пока ответ с сервера не пришёл, не рисуем ничего: иначе главная мелькала
   * бы на долю секунды перед онбордингом. Ошибка запроса — не повод держать
   * пустой экран, тогда показываем витрину как есть.
   */
  if (onboardingApplies && me.isPending) return null;

  if (onboardingApplies && me.data?.onboarded === false) {
    return <Navigate to="/onboarding/start" replace />;
  }

  return (
    <>
      <Routes>
        <Route path="/" element={<HomeScreen />} />
        <Route path="/onboarding" element={<Navigate to="/onboarding/start" replace />} />
        <Route path="/onboarding/:step" element={<OnboardingScreen />} />
        <Route path="/checkout" element={<CheckoutScreen />} />
        <Route path="/orders/:orderNumber/placed" element={<OrderPlacedScreen />} />
        <Route path="/orders/:orderNumber" element={<OrderScreen />} />
        <Route path="/articles" element={<ArticlesScreen />} />
        <Route path="/articles/:slug" element={<ArticleScreen />} />
        <Route path="/profile" element={<ProfileScreen />} />
        <Route path="/profile/edit" element={<ProfileEditScreen />} />
        <Route path="/profile/orders" element={<OrdersScreen />} />
        <Route path="/faq" element={<FaqScreen />} />
        {/* Роут не скрыт от роутера — скрыт от навигации. Защита живёт на сервере. */}
        <Route path="/admin" element={<AdminOrdersScreen />} />
        <Route path="/admin/orders/:orderNumber" element={<AdminOrderScreen />} />
        <Route path="*" element={<div className="state">Страница не найдена</div>} />
      </Routes>

      {isTab && <TabBar />}
      <SdkInit />
    </>
  );
}
